"use client";

import { useEffect } from "react";

/* ChunkLoadError recovery — seen twice in PSI runs as
 * "Failed to load chunk .../chunks/<hash>.js ... net::ERR_CONNECTION_FAILED".
 * Classic cause: a visitor's HTML references one deployment's chunk hashes,
 * then a newer deploy replaces those assets before a lazy import() fires (more
 * likely here given how frequently this app ships). Standard, safe mitigation
 * regardless of the exact cause: catch the failure and reload ONCE to fetch
 * the current HTML + asset manifest. Guarded by sessionStorage so a genuinely
 * persistent failure doesn't reload-loop the visitor.
 *
 * Deliberately tiny and NOT dynamically imported itself — it has to be active
 * from first paint to catch a failure on any later lazy-loaded chunk. */
const RELOAD_GUARD_KEY = "apex-chunk-reload-attempted";
const CHUNK_ERROR_PATTERN = /Loading chunk|ChunkLoadError|Failed to load chunk/i;

function isChunkLoadFailure(message: unknown): boolean {
  return typeof message === "string" && CHUNK_ERROR_PATTERN.test(message);
}

function recoverOnce() {
  if (typeof window === "undefined") return;
  try {
    if (sessionStorage.getItem(RELOAD_GUARD_KEY) === "1") return; // already tried this session
    sessionStorage.setItem(RELOAD_GUARD_KEY, "1");
  } catch {
    return; // sessionStorage unavailable — don't risk a reload loop
  }
  window.location.reload();
}

export function ChunkErrorRecovery() {
  useEffect(() => {
    const onError = (e: ErrorEvent) => {
      if (isChunkLoadFailure(e.message) || isChunkLoadFailure(e.error?.message)) recoverOnce();
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      const reason = e.reason as { message?: unknown; name?: unknown } | undefined;
      if (isChunkLoadFailure(reason?.message) || isChunkLoadFailure(reason?.name)) recoverOnce();
    };
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return null;
}
